/* eslint-disable camelcase */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Heading,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router';

import { utils as commonUtils } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  orderLoanAccountsLoans,
  ValleyBankArrangementGroup,
  parse,
  generateReceivableStats,
  getTotalLoanOutstanding,
  calculateMarginAvailabilityRate,
  calculateMarginAvailability,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankArrangementGroupLazyQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { CollateralAccountsTable } from './CollateralAccountsTable';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { colors } from '~/packages/common-frontend/colors';
import {
  Alert,
  ProgressAlert,
} from '~/packages/common-frontend/components/Alert';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IconTag } from '~/packages/common-frontend/components/Tag';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import {
  ArrangementGroupTag,
  useArrangmentGroupUrlParam,
} from '~/valleyBank/domains/ArrangementGroups/hooks/ArrangementGroupsContext';
import { ArrangementGroupSearch } from '~/valleyBank/domains/ArrangementGroups/ui/ArrangementGroupSearch';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { LoanAccountTag } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { LoansTable } from '~/valleyBank/domains/LoanAccounts/ui/LoansTable';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';

// Fragments
const RouteBase = () => {
  const toast = useToast();
  const { reportingDate, isPreview } = useDataMetadataContext();
  const { arrangementGroupNumber } = useArrangmentGroupUrlParam();

  const [
    arrangementGroup,
    setArrangementGroup,
  ] = useState<ValleyBankArrangementGroup | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);

  const [
    getArrangementGroup,
    { error },
  ] = useGetValleyBankArrangementGroupLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      try {
        if (res.valleyBankArrangementGroup === null) {
          setNotFound(true);
        }
        if (res?.valleyBankArrangementGroup) {
          const newArrangementGroup = parse.parseValleyBankArrangementGroup(
            res.valleyBankArrangementGroup,
          );
          if (newArrangementGroup) {
            // @jendevelops TODO add as of date when fetch arrangement group
            setArrangementGroup(newArrangementGroup);
          }
        }
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (
      arrangementGroupNumber &&
      arrangementGroupNumber !== arrangementGroup?.arrangementGroupNumber
    ) {
      setNotFound(false);
      setIsLoading(true);
      setArrangementGroup(null);
      getArrangementGroup({
        variables: {
          arrangementGroupNumber,
          isPreview,
          reportingDate,
        },
      });
    }
  }, [
    getArrangementGroup,
    isPreview,
    arrangementGroup,
    arrangementGroupNumber,
    reportingDate,
  ]);

  const collateralAccounts = arrangementGroup?.collateralAccounts || [];

  const receivables =
    arrangementGroup?.collateralAccounts.flatMap(
      ({ receivables: _receivables }) => _receivables,
    ) || [];
  const [assignedReceivables, unassignedReceivables] = _.partition(
    receivables,
    (receivable) => Boolean(receivable.loan_account_number),
  );

  const loanParties =
    arrangementGroup?.loanAccounts.flatMap(
      ({ loanParties: _loanParties, asOfDate }) =>
        _loanParties.map((loanParty) => ({ ...loanParty, asOfDate })),
    ) || [];

  const {
    totalPreAllocationLendableValue: unassignedTotalPreAllocationLendableValue,
    totalMarketValue: unassignedTotalMarketValue,
  } = generateReceivableStats(unassignedReceivables);

  const {
    totalPostAllocationLendableValue: assignedTotalPostAllocationLendableValue,
    totalMarketValue: assignedTotalMarketValue,
  } = generateReceivableStats(assignedReceivables);

  const totalLoanOutstanding = getTotalLoanOutstanding(
    arrangementGroup?.loanAccounts || [],
  );

  const marginAvailability = calculateMarginAvailability({
    collateralLendableValue: assignedTotalPostAllocationLendableValue,
    loanOutstanding: totalLoanOutstanding,
  });

  const marginAvailabilityRate = calculateMarginAvailabilityRate({
    collateralLendableValue: assignedTotalPostAllocationLendableValue,
    loanOutstanding: totalLoanOutstanding,
  });

  return (
    <VStack w="100%" spacing="24px" alignItems="flex-start">
      <Heading as="h1" fontSize="4xl" mb="60px">
        Arrangement Groups
      </Heading>
      <ArrangementGroupSearch />
      {isLoading && <ProgressAlert>Loading arrangement group</ProgressAlert>}
      {!arrangementGroup && notFound && (
        <Alert status="warning">
          <Text>Arrangement group not found</Text>
        </Alert>
      )}
      {!isLoading && arrangementGroupNumber && arrangementGroup && (
        <>
          <ArrangementGroupTag arrangmentGroup={arrangementGroupNumber} />

          <Flex gridRowGap="24px" gridColumnGap="24px" w="100%" flexWrap="wrap">
            <InfoCard
              title="Total Loan Outstanding"
              value={formatCurrencyAmount(
                totalLoanOutstanding,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              w="400px"
            />
            <InfoCard
              title="Total Loan Accounts"
              value={String(arrangementGroup?.loanAccounts.length || 0)}
              icon={<Icon icon="hashtag" size="24px" />}
              w="400px"
            />
            <InfoCard
              title="Total Assigned Market Value"
              value={formatCurrencyAmount(
                assignedTotalMarketValue,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              w="400px"
            />
            <InfoCard
              title="Total Assigned Lendable Value"
              value={formatCurrencyAmount(
                assignedTotalPostAllocationLendableValue,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              w="400px"
            />
            <InfoCard
              title="Margin Availability"
              value={formatCurrencyAmount(
                marginAvailability,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              w="400px"
            />
            <InfoCard
              title="Margin Availability Rate"
              value={percentageToPercentageLabel(
                marginAvailabilityRate * 100,
                2,
              )}
              icon={<Icon icon="percentage" size="24px" />}
              w="400px"
            />
          </Flex>

          <Heading as="h2" fontSize="24px" mb="24px">
            Customers
          </Heading>
          {arrangementGroup.customers.length === 0 && (
            <Alert status="info">
              <Text>No customers associated with arrangement group</Text>
            </Alert>
          )}
          {arrangementGroup.customers.length > 0 && (
            <TableContainer w="100%">
              <Table w="100%">
                <Thead>
                  <Tr>
                    <Th>Customer CIF Number</Th>
                    <Th>Customer</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {arrangementGroup.customers.map(({ cif_num, full_name }) => (
                    <Tr key={cif_num}>
                      <Td>
                        <CustomerSummaryLink cifnum={cif_num}>
                          {cif_num}
                        </CustomerSummaryLink>
                      </Td>
                      <Td>
                        <CustomerSummaryLink cifnum={cif_num}>
                          {full_name}
                        </CustomerSummaryLink>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
          <Heading as="h2" fontSize="24px" mb="24px">
            Loan Accounts
          </Heading>
          {arrangementGroup.loanAccounts.length === 0 && (
            <Alert status="info">
              <Text>No loan accounts associated with arrangement group</Text>
            </Alert>
          )}
          {arrangementGroup.loanAccounts.length > 0 && (
            <Accordion
              defaultIndex={[0]}
              allowMultiple
              w="100%"
              border="1px"
              borderRadius="8px"
              borderColor={colors.gray[200]}
            >
              {arrangementGroup.loanAccounts.map((loanAccount) => {
                const {
                  loanAccountNumber,
                  principalBalance,
                  accountName,
                  arrangementGroupId,
                } = loanAccount;
                return (
                  <AccordionItem key={loanAccountNumber} p="24px">
                    <AccordionButton>
                      <HStack w="100%" justify="flex-start">
                        <Heading as="h3" fontSize="20px" fontWeight="bold">
                          {`${accountName || '-'}`}
                        </Heading>
                        <IconTag
                          icon="dollar"
                          text={`Loan Outstanding: ${
                            formatCurrencyAmount(
                              principalBalance,
                              Bank.Currency.USD,
                            ) || '-'
                          }`}
                        />
                      </HStack>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel mt="40px">
                      <HStack
                        w="100%"
                        justify="flex-start"
                        spacing="16px"
                        mb="32px"
                      >
                        <LoanAccountTag loanAccountId={loanAccountNumber} />
                        <ArrangementGroupTag
                          arrangmentGroup={arrangementGroupId}
                        />
                      </HStack>
                      <LoansTable
                        loans={orderLoanAccountsLoans([loanAccount])}
                        noLoansMessage="There are no loans associated with this arrangement group."
                        showStats={false}
                        isLoading={isLoading}
                        isError={Boolean(error)}
                        downloadFilenamePrefix={`loan-accounts-arrangement-group-${arrangementGroupNumber}`}
                        pageType="arrangement-groups"
                      />
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}

          <Heading as="h2" fontSize="24px" mb="24px">
            Collateral Accounts
          </Heading>
          {arrangementGroup.collateralAccounts.length === 0 && (
            <Alert status="info">
              <Text>
                No collateral accounts associated with arrangement group
              </Text>
            </Alert>
          )}

          {arrangementGroup.collateralAccounts.length > 0 && (
            <>
              <HStack spacing="24px" w="100%">
                <InfoCard
                  title="Total Assigned Market Value"
                  value={formatCurrencyAmount(
                    assignedTotalMarketValue,
                    Bank.Currency.USD,
                  )}
                  icon={<Icon icon="dollar" size="24px" />}
                  flexGrow={1}
                />
                <InfoCard
                  title="Total Assigned Lendable Value"
                  value={formatCurrencyAmount(
                    assignedTotalPostAllocationLendableValue,
                    Bank.Currency.USD,
                  )}
                  icon={<Icon icon="dollar" size="24px" />}
                  flexGrow={1}
                />
                <InfoCard
                  title="Total Unassigned Market Value"
                  value={formatCurrencyAmount(
                    unassignedTotalMarketValue,
                    Bank.Currency.USD,
                  )}
                  icon={<Icon icon="dollar" size="24px" />}
                  flexGrow={1}
                />
                <InfoCard
                  title="Total Unassigned Lendable Value"
                  value={formatCurrencyAmount(
                    unassignedTotalPreAllocationLendableValue,
                    Bank.Currency.USD,
                  )}
                  icon={<Icon icon="dollar" size="24px" />}
                  flexGrow={1}
                />
              </HStack>
              <CollateralAccountsTable
                arrangementGroupNumber={arrangementGroupNumber}
                collateralAccounts={collateralAccounts}
                isLoading={isLoading}
                isError={Boolean(error)}
                loanParties={loanParties}
              />
            </>
          )}
        </>
      )}
    </VStack>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="./loan-accounts" />;
    }}
  >
    <Flex direction="column" padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}>
      <RouteBase />
    </Flex>
  </RouteErrorBoundary>
);
